import { getApiUrl } from "../config/api";

/** TikTok Pixel ID — override with REACT_APP_TIKTOK_PIXEL_ID in .env if needed */
export const TIKTOK_PIXEL_ID =
  process.env.REACT_APP_TIKTOK_PIXEL_ID || "D8552A3C77UEN23N07CG";

const CURRENCY = "MAD";

export type TikTokContentItem = {
  content_id: string;
  content_name?: string;
  content_type?: string;
  quantity?: number;
  price?: number;
};

type TtqQueue = {
  track: (
    event: string,
    props?: Record<string, unknown>,
    options?: { event_id?: string }
  ) => void;
  page: () => void;
  identify?: (props: Record<string, unknown>) => void;
  load?: (id: string, opts?: Record<string, unknown>) => void;
};

declare global {
  interface Window {
    ttq?: TtqQueue;
  }
}

function getTtq(): TtqQueue | null {
  if (typeof window === "undefined" || !window.ttq) return null;
  return window.ttq;
}

function getCookie(name: string): string {
  if (typeof document === "undefined") return "";
  const match = document.cookie.match(new RegExp(`(?:^|; )${name}=([^;]*)`));
  return match ? decodeURIComponent(match[1]) : "";
}

/** TikTok click / cookie ids for Events API matching */
export function getTikTokAttribution(): { ttclid: string; ttp: string } {
  if (typeof window === "undefined") {
    return { ttclid: "", ttp: "" };
  }
  const params = new URLSearchParams(window.location.search);
  return {
    ttclid: params.get("ttclid") || getCookie("ttclid") || "",
    ttp: getCookie("_ttp") || "",
  };
}

function tiktokEventsApiUrl(): string {
  if (typeof window !== "undefined") {
    const origin = window.location.origin;
    if (origin === "https://www.kissariti.ma" || origin === "https://kissariti.ma") {
      return "/api/tiktok_events.php";
    }
  }
  return getApiUrl("TIKTOK_EVENTS");
}

/** SHA-256 hex digest (browser pixel identify). */
export async function sha256Hex(value: string): Promise<string> {
  const data = new TextEncoder().encode(value);
  const hash = await crypto.subtle.digest("SHA-256", data);
  return Array.from(new Uint8Array(hash))
    .map((b) => b.toString(16).padStart(2, "0"))
    .join("");
}

function normalizeEmail(email: string): string {
  return email.trim().toLowerCase();
}

function normalizePhone(phone: string): string {
  let digits = phone.replace(/\D/g, "");
  if (!digits) return "";
  if (digits.startsWith("00")) digits = digits.slice(2);
  if (digits.startsWith("0")) digits = `212${digits.slice(1)}`;
  if (!digits.startsWith("212") && digits.length === 9) digits = `212${digits}`;
  return digits;
}

function toContentsArray(items: TikTokContentItem[]) {
  return items.map((item) => ({
    content_id: String(item.content_id),
    content_type: item.content_type || "product",
    content_name: item.content_name || "",
    ...(item.quantity != null ? { quantity: item.quantity } : {}),
    ...(item.price != null ? { price: item.price } : {}),
  }));
}

function buildEventPayload(value: number, contents: TikTokContentItem[]) {
  return {
    contents: toContentsArray(contents),
    value: Number(value) || 0,
    currency: CURRENCY,
  };
}

export function tiktokEventId(orderId: string, eventName: string): string {
  return `${eventName}_${orderId}`.replace(/\s+/g, "_");
}

type ServerEventOpts = {
  event: string;
  eventId?: string;
  value?: number;
  contents?: TikTokContentItem[];
  search_string?: string;
  email?: string;
  phone?: string;
  external_id?: string;
};

/** POST to Kissariti API → TikTok Events API v1.3 (server-side). */
function sendTikTokServerEvent(opts: ServerEventOpts): void {
  if (typeof window === "undefined") return;
  const { ttclid, ttp } = getTikTokAttribution();
  const body: Record<string, unknown> = {
    event: opts.event,
    event_id: opts.eventId,
    event_time: Math.floor(Date.now() / 1000),
    value: opts.value ?? 0,
    currency: CURRENCY,
    contents: opts.contents ? toContentsArray(opts.contents) : [],
    url: window.location.href.split("#")[0],
    ttclid: ttclid || undefined,
    ttp: ttp || undefined,
  };
  if (opts.search_string) body.search_string = opts.search_string;
  if (opts.email?.trim()) body.email = opts.email.trim();
  if (opts.phone?.trim()) body.phone = opts.phone.trim();
  if (opts.external_id?.trim()) body.external_id = opts.external_id.trim();

  fetch(tiktokEventsApiUrl(), {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
    keepalive: true,
  }).catch(() => {});
}

function tiktokTrackEvent(
  event: string,
  properties: Record<string, unknown>,
  eventId?: string,
  serverOpts?: Omit<ServerEventOpts, "event" | "eventId">
): void {
  try {
    const ttq = getTtq();
    if (ttq?.track) {
      if (eventId) {
        ttq.track(event, properties, { event_id: eventId });
      } else {
        ttq.track(event, properties);
      }
    }
  } catch {
    /* optional */
  }

  sendTikTokServerEvent({
    event,
    eventId,
    value: Number(properties.value) || 0,
    contents: (properties.contents as TikTokContentItem[]) || serverOpts?.contents,
    search_string:
      (properties.search_string as string) || serverOpts?.search_string,
    email: serverOpts?.email,
    phone: serverOpts?.phone,
    external_id: serverOpts?.external_id,
  });
}

export async function tiktokIdentifyUser(opts: {
  email?: string;
  phone?: string;
  externalId?: string;
}): Promise<void> {
  const ttq = getTtq();
  if (!ttq?.identify) return;

  const payload: Record<string, string> = {};

  if (opts.email?.trim()) {
    payload.email = await sha256Hex(normalizeEmail(opts.email));
  }
  if (opts.phone?.trim()) {
    const normalized = normalizePhone(opts.phone);
    if (normalized) {
      payload.phone_number = await sha256Hex(normalized);
    }
  }
  if (opts.externalId?.trim()) {
    payload.external_id = await sha256Hex(opts.externalId.trim());
  }

  if (Object.keys(payload).length === 0) return;

  try {
    ttq.identify(payload);
  } catch {
    /* optional */
  }
}

export function tiktokPage(): void {
  try {
    getTtq()?.page?.();
  } catch {
    /* optional */
  }
}

export function tiktokViewContent(opts: {
  contentId: string;
  contentName: string;
  value?: number;
}): void {
  const contents: TikTokContentItem[] = [
    {
      content_id: opts.contentId,
      content_name: opts.contentName,
      content_type: "product",
    },
  ];
  tiktokTrackEvent("ViewContent", buildEventPayload(opts.value ?? 0, contents));
}

export function tiktokAddToCart(opts: {
  contentId: string;
  contentName: string;
  quantity?: number;
  value?: number;
}): void {
  const contents: TikTokContentItem[] = [
    {
      content_id: opts.contentId,
      content_name: opts.contentName,
      content_type: "product",
      quantity: opts.quantity ?? 1,
      price:
        opts.value != null && opts.quantity
          ? opts.value / opts.quantity
          : undefined,
    },
  ];
  tiktokTrackEvent("AddToCart", buildEventPayload(opts.value ?? 0, contents));
}

export function tiktokInitiateCheckout(opts: {
  value: number;
  contents: TikTokContentItem[];
}): void {
  tiktokTrackEvent(
    "InitiateCheckout",
    buildEventPayload(opts.value, opts.contents)
  );
}

export function tiktokAddPaymentInfo(opts: {
  value: number;
  contents: TikTokContentItem[];
}): void {
  tiktokTrackEvent(
    "AddPaymentInfo",
    buildEventPayload(opts.value, opts.contents)
  );
}

export function tiktokSearch(opts: {
  searchString: string;
  value?: number;
  contents?: TikTokContentItem[];
}): void {
  const contents =
    opts.contents?.length ?
      opts.contents
    : [
        {
          content_id: "search",
          content_name: opts.searchString,
          content_type: "product",
        },
      ];
  tiktokTrackEvent("Search", {
    ...buildEventPayload(opts.value ?? 0, contents),
    search_string: opts.searchString,
  });
}

const CONVERSION_STORAGE_PREFIX = "kissariti_ttq_order_";

export async function tiktokTrackOrderPlaced(opts: {
  orderId: string;
  value: number;
  contents: TikTokContentItem[];
  email?: string;
  phone?: string;
  externalId?: string;
}): Promise<void> {
  if (!opts.orderId) return;

  const dedupeKey = `${CONVERSION_STORAGE_PREFIX}${opts.orderId}`;
  try {
    if (
      typeof sessionStorage !== "undefined" &&
      sessionStorage.getItem(dedupeKey)
    ) {
      return;
    }
    sessionStorage.setItem(dedupeKey, "1");
  } catch {
    /* continue */
  }

  try {
    await tiktokIdentifyUser({
      email: opts.email,
      phone: opts.phone,
      externalId: opts.externalId || opts.orderId,
    });
  } catch {
    /* must not block order confirmation */
  }

  const payload = buildEventPayload(opts.value, opts.contents);
  const placeOrderEventId = tiktokEventId(opts.orderId, "PlaceAnOrder");
  const purchaseEventId = tiktokEventId(opts.orderId, "Purchase");
  const serverUser = {
    email: opts.email,
    phone: opts.phone,
    external_id: opts.externalId || opts.orderId,
  };

  tiktokTrackEvent("PlaceAnOrder", payload, placeOrderEventId, serverUser);
  tiktokTrackEvent("Purchase", payload, purchaseEventId, serverUser);
}
